import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SEO } from '../../components-new/SEO/SEO';
import { AnimatedSection } from '../../components-new/AnimatedSection/AnimatedSection';
import { ProjectShowcase } from '../../components-new/ProjectShowcase/ProjectShowcase';
import { projects } from '../../data/projects';
import './style.less';

export const Projects = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Scroll to project when URL contains a hash
  useEffect(() => {
    if (!location.hash) return;

    const projectId = location.hash.slice(1);

    // Validate hash against known project IDs
    const validProjectIds = projects.map((p) => p.id);
    if (!validProjectIds.includes(projectId)) return;

    const element = document.getElementById(projectId);
    if (!element) return;

    let rafId;
    const scrollToElement = () => {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    };

    rafId = requestAnimationFrame(scrollToElement);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [location.hash]);

  const scrollToProjects = () => {
    const heroElement = document.querySelector('.page-hero');
    if (heroElement) {
      const heroBottom = heroElement.getBoundingClientRect().bottom + window.scrollY;
      window.scrollTo({ top: heroBottom, behavior: 'smooth' });
    }
  };

  return (
    <div className="projects-page">
      <SEO
        title={t('projects.seo.title')}
        description={t('projects.seo.description')}
        keywords={t('projects.seo.keywords')}
      />

      {/* Hero Section */}
      <section
        className="page-hero"
        style={{ backgroundImage: `url('/images/projects/project1/diningroom.jpg')` }}
      >
        <div className="hero-overlay" aria-hidden="true"></div>
        <div className="container">
          <AnimatedSection className="hero-content">
            <span className="hero-subtitle">{t('projects.heroSubtitle', 'Портфолио')}</span>
            <h1>{t('projects.title')}</h1>
            <p>{t('projects.subtitle')}</p>
            <div className="hero-line" aria-hidden="true"></div>
          </AnimatedSection>
        </div>
        <button
          className="hero-scroll-indicator"
          onClick={scrollToProjects}
          aria-label={t('projects.scrollToProjects', 'Превъртете към проектите')}
          tabIndex={0}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            aria-hidden="true"
          >
            <i className="fas fa-chevron-down" aria-hidden="true"></i>
          </motion.div>
        </button>
      </section>

      {/* Projects Showcase */}
      <section className="projects-showcase">
        {projects.map((project, index) => (
          <ProjectShowcase
            key={project.id}
            id={project.id}
            title={t(`projects.${project.translationKey}.title`)}
            style={t(`projects.${project.translationKey}.style`)}
            location={t(`projects.${project.translationKey}.location`)}
            description={t(`projects.${project.translationKey}.description`, { defaultValue: '' })}
            mainImage={project.mainImage}
            gallery={project.gallery}
            reversed={index % 2 === 1}
            index={index}
          />
        ))}
      </section>
    </div>
  );
};
